import React, {useState} from 'react';
import {Button, Checkbox, HelpIcon} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import EventSetsListPalette from './EventSetsListPalette';
import {useSelector} from 'react-redux';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';
import {useExperience} from '../../Experience/ExperienceProvider';
import {CalendarOptions, EventSetCategory, EventSetData, EventSetLink} from '@taopix/taopix-core';
import {CalendarEventSetLinkSelector} from '../../../Selectors';
import {useTranslation} from 'react-i18next';

export type EventSetsPaletteProps = {
  sectionKey: string;
  calendarSettings: CalendarOptions;
};

export type EventSetsDataType = {
  date: number;
  name: string;
  ref: string;
};

const EventSetsPalette = ({sectionKey, calendarSettings}: EventSetsPaletteProps) => {
  const ui = useUiState();
  const {experience} = useExperience();
  const {t} = useTranslation(['calendars']);

  const calendarManager = window.Taopix.Core.get('calendarManager');
  const showPredefinedEventSets =
    calendarSettings?.canAddPredefinedEvents && experience.EDITOR.calendarEvents.showEventSets;

  const calendarEventSets = calendarManager?.getEventSets(); // list of all eventSets

  const calendarEventSetLinks = useSelector(CalendarEventSetLinkSelector);

  const [selectedEventSet, setSelectedEventSet] = useState<EventSetData>();

  const sectionButtonClasses = classNames('py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'lg:flex-1',
    'space-y-sm',
    'sm:pb-md',
    'overflow-y-auto',
    'pt-md'
  );

  // if only one event sets category then list below as check boxes
  // if more than one have panels which link to palette to display list of eventSets in category
  // if more than one eventSet category then return the palette buttons for each category

  // return the eventSet depending on if it is applied or not
  const getEventSetLink = (eventSetKey: string, eventSetData: any) => {
    if (Object.keys(calendarEventSetLinks).includes(eventSetKey)) {
      return calendarEventSetLinks[eventSetKey];
    } else {
      return eventSetData;
    }
  };

  // toggle event set
  const toggleEventSet = (eventSetRef: string, eventSetLink: EventSetLink) => {
    if (showPredefinedEventSets && eventSetLink?.canRemove) {
      ui.dispatch(UserInterfaceActions.setIsBusy(true));
      calendarManager.toggleEventSet({ref: eventSetRef}).then(() => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });
    }
  };

  // set selected eventset and go to next palette to display dates
  const onMoreInfoClick = (
    eventSetData: EventSetData,
    eventSetLink: EventSetLink,
    category: string,
    noCategoryPalete?: boolean
  ) => {
    setSelectedEventSet({
      ...eventSetData,
      canRemove: eventSetLink.canRemove,
    });
    // if the jump has been made from the main palette to the event sets list palette set path accordingly
    if (noCategoryPalete) {
      ui.dispatch(
        UserInterfaceActions.setToolPaletteSection(
          `calendar-palette/events-set-list-${eventSetData.setUniqueRef}`
        )
      );
    } else {
      ui.dispatch(
        UserInterfaceActions.setToolPaletteSection(
          `calendar-palette/${sectionKey}-${category}/events-set-list-${eventSetData.setUniqueRef}`
        )
      );
    }
  };

  const renderEventSets = (category: string, categoryData: EventSetCategory) => {
    return Object.entries(categoryData.eventSets).map(
      ([eventSetKey, eventSetData]: [string, EventSetData], index: number) => {
        const eventSetLink = getEventSetLink(eventSetKey, eventSetData);
        const isApplied = Object.keys(calendarEventSetLinks).includes(eventSetKey);
        // if cannot add predefined events and the event set is not applied by default do not show
        if (showPredefinedEventSets || isApplied) {
          return (
            <div
              className={'flex justify-between items-center'}
              key={`event-${eventSetKey}-${index}`}
            >
              <Checkbox
                id={`event-set-id-${eventSetKey}-${index}`}
                key={`event-set-selected-${eventSetKey}-${isApplied}-${index}`}
                label={eventSetData?.name as unknown as string}
                defaultChecked={Object.keys(calendarEventSetLinks)?.includes(eventSetKey)}
                preventManualUncheck={!eventSetLink.canRemove}
                disabled={!showPredefinedEventSets}
                onChange={(checked: boolean) => {
                  toggleEventSet(eventSetData?.setUniqueRef, eventSetLink);
                }}
              />
              <Button
                label={t('calendars:str_LabelViewEventSet', {
                  defaultValue: 'View event set',
                })}
                className={'ml-auto'}
                hideLabel
                size={'small'}
                buttonStyle={'standard'}
                endIcon={<HelpIcon />}
                onClick={() => {
                  // pass selected eventSet ref and name into list palette
                  onMoreInfoClick(eventSetData, eventSetLink, category, true);
                }}
              />
            </div>
          );
        } else {
          return <></>;
        }
      }
    );
  };

  return (
    <>
      {/* if more than one catgory return a palette */}
      {calendarEventSets && Object.keys(calendarEventSets).length > 1 ? (
        <>
          {Object.entries(calendarEventSets).map(
            ([category, categoryData]: [string, EventSetCategory], index: number) => {
              // if the event set is not categorised then return a checkbox
              if (category === 'nocategory') {
                return renderEventSets(category, categoryData);
                // if the event is categorised return a palette containing all events in category
              } else {
                return (
                  <ToolPaletteSection
                    id={'calendarEventSetsPalette'}
                    key={`calendarEventSetsPalette-${index}`}
                    title={categoryData?.localisedName as unknown as string}
                    sectionKey={`${sectionKey}-${category}`}
                    buttonClasses={sectionButtonClasses}
                    subSection
                    showButton
                    buttonLayout={'horizontal'}
                    subSectionPanelType={'pop-up'}
                    smallScreenPanelHeight={'full'}
                    parentselected={true}
                  >
                    <div className={wrapperClasses}>{renderEventSets(category, categoryData)}</div>
                  </ToolPaletteSection>
                );
              }
            }
          )}
        </>
      ) : (
        // if only one category return a checkbox for the eventset
        <>
          {calendarEventSets &&
            Object.entries(calendarEventSets).map(
              ([category, categoryData]: [string, EventSetCategory]) => {
                return renderEventSets(category, categoryData);
              }
            )}
        </>
      )}
      <EventSetsListPalette
        sectionKey={'events-set-list'}
        selectedEventSet={selectedEventSet as EventSetData}
        calendarSettings={calendarSettings}
      />
    </>
  );
};

export default EventSetsPalette;
